import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { BASE_PATH, Gateway } from '@wheere/api-site';
import { errorMessage } from '@wheere-front/shared-util';
import { catchError, concat, last, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { frequencyRejection, rejectionLines } from '../util/frequency-rejection';
import { SITE_CONFIG_HOST, SiteConfigHost } from './site-config-host';

export interface GatewayPosition {
  latitude: number | undefined;
  longitude: number | undefined;
  altitude: number | undefined;
}

export interface SiteGatewayData {
  gatewayId: string;
  orderSite: number;
  latitude: number;
  longitude: number;
  altitude: number;
  calib?: number[][];
  freq?: number[];
  theta?: number[];
  size?: number;
}

export interface SizeOption {
  label: string;
  /** Libellé à traduire (transloco) quand il n'est pas qu'une valeur technique ; prime sur `label`. */
  labelKey?: string;
  value: number;
  freqValue: number;
}

/**
 * Résultat du save : `error` = message métier du backend (affiché tel quel), `errorKey` = clé
 * transloco d'un refus local.
 */
export interface SaveLayoutResult {
  success: boolean;
  error?: string;
  errorKey?: string;
  /** Refus 422 `FrequencyFileRejection` (dépôt d'un fichier ou validation bloquée) : un motif par
   * ligne, messages du serveur. Absent pour les autres erreurs. */
  errors?: string[];
}

/** Fichiers déposés au save (brouillon du site, ADR-039) : seuls ceux importés depuis le dernier save. */
export interface SaveLayoutFiles {
  frequencyRawContent?: string;
  frequencyFileName?: string;
  globalCalibRawContent?: string;
  globalCalibFileName?: string;
}

/**
 * Unified layout body — the SAME contract the integrator sends to
 * {@code PATCH /sites/{id}/layout}, the single configuration path since ADR-019
 * (per-gateway antennas + RF OUT pins included).
 */
export interface LayoutGatewayRequest {
  gatewayId: string;
  orderSite: number;
  latitude: number;
  longitude: number;
  altitude: number;
}
export interface LayoutAntennaRequest {
  orderGateway: number;
  latitude: number;
  longitude: number;
  altitude: number;
  /** Explicit RF OUT pin (0..3). Omitted ⇒ backend default = orderGateway. */
  rfOut?: number;
}
export interface LayoutGatewayAntennas {
  gatewayId: string;
  antennas: LayoutAntennaRequest[];
}
export interface SiteConfigLayoutBody {
  size?: number;
  comment?: string;
  gateways: LayoutGatewayRequest[];
  gatewayAntennas: LayoutGatewayAntennas[];
  presetId?: string;
}

/**
 * V8 unified topology — each gateway has 1..4 antennas. Replaces the old
 * "flat antennaPositions array exists only for single-gateway sites" shape.
 *
 * `rfOut` is the explicit hardware RF OUT pin (0..3). When `null` the firmware falls
 * back to `orderGateway` as the implicit pin (preserves the legacy "antenna i → RF OUT i"
 * mapping for free).
 */
export interface AntennaDraft {
  orderGateway: number;
  latitude: number | undefined;
  longitude: number | undefined;
  altitude: number | undefined;
  rfOut: number | null;
}

/** Position-only view kept for backward-compat with PositionDialog / RTK callers. */
export interface AntennaPosition {
  latitude: number | undefined;
  longitude: number | undefined;
  altitude: number | undefined;
}

export interface ConfigReadiness {
  hardware: { done: boolean; label: string };
  positions: { done: boolean; label: string };
  /** Fichier de fréquences + calib (ADR-039) ; `blocked` = motif de blocage, voir la section Fichiers. */
  files: { done: boolean; count: number; total: number; label: string; blocked?: boolean };
  size: { done: boolean };
  canValidate: boolean;
}

// V8 audit T6 — was `providedIn: 'root'`, which made the service a global singleton. That
// broke when an admin opened two site-config pages in parallel browser tabs: the
// `selectedGateways`, draft localStorage signals all bled across the two
// instances. Now scoped at SiteConfigComponent level (see `providers: [SiteConfigurationService]`
// there) so each route mount gets its own clean instance and Angular disposes it on
// navigation away.
@Injectable()
export class SiteConfigurationService {
  /**
   * Application hôte (inside : `SitesStore`, integrator : adaptateur). Optionnelle à l'injection
   * pour que le save se teste seul ; tout usage sans hôte échoue explicitement (`sitesStore`).
   */
  private readonly host: SiteConfigHost | null = this.resolveHost();
  /** Inside la redéfinit pour lire son `SitesStore`. */
  protected resolveHost(): SiteConfigHost | null {
    return inject(SITE_CONFIG_HOST, { optional: true });
  }
  private get sitesStore(): SiteConfigHost {
    if (!this.host) throw new Error('SiteConfigurationService: no SITE_CONFIG_HOST provided');
    return this.host;
  }
  /**
   * V7-fix: raw HTTP for `/gateways/without-site` until the
   * generated angular clients (`@wheere/api-site` 3.3.0) carry the new `organizationId` query
   * param. The backend now requires it for PLATFORM_* users (orphan pickers don't make sense
   * across all orgs), so we send it explicitly here.
   */
  private http = inject(HttpClient);
  private apiBasePath = inject(BASE_PATH);

  // Signals replacing BehaviorSubjects
  readonly siteId = signal<string | null>(null);
  readonly availableGateways = signal<Gateway[]>([]);
  readonly selectedGateways = signal<Gateway[]>([]);
  /** Fichier de fréquences (JSON freq / freq_delta / theta, ADR-039) en place ou choisi. */
  readonly frequenciesLoaded = signal<boolean>(false);
  readonly globalCalibLoaded = signal<boolean>(false);
  /** La section Fichiers a un motif bloquant (freq_delta insuffisants, calib, DAC partagé…). */
  readonly frequencyBlocked = signal<boolean>(false);
  readonly selectedSize = signal<SizeOption | null>(null);

  /**
   * V8 unified topology — antennas keyed by gatewayId. Each gateway carries its own
   * 1..4 antennas (replacing the legacy flat single-gateway `antennaPositions` signal
   * that only existed for single-gateway sites).
   *
   * Initialised lazily when a gateway is added: if `Gateway.antennas` came from the
   * backend they're hydrated as-is; otherwise we seed 1 antenna at the gateway's
   * coordinates.
   */
  readonly gatewayAntennas = signal<Record<string, AntennaDraft[]>>({});

  /**
   * V8 — per-gateway "custom gateway position" flag, keyed by gatewayId. Mirrors the
   * integrator: a single-antenna gateway defaults to OFF → its chassis position is
   * implicit (= antenna 0), with no separate position row on the card and no distinct
   * marker on the map. ON → the operator places the gateway chassis independently of
   * antenna 0. Multi-antenna gateways always expose their own position (see
   * {@link isGatewayPositionCustom}) regardless of this flag.
   */
  readonly customGatewayPosition = signal<Record<string, boolean>>({});

  readonly SIZES: SizeOption[] = [
    { label: '1024 Hz', value: 1048576, freqValue: 1024 },
    { label: '128 Hz', value: 8388608, freqValue: 128 },
    { label: '64 Hz', value: 16777216, freqValue: 64 },
    { label: '32 Hz', value: 33554432, freqValue: 32 },
    { label: '16 Hz', value: 67108864, freqValue: 16 },
    { label: 'No Limit RF OUT', labelKey: 'siteConfiguration.files.sizeNoLimit', value: 67108864, freqValue: 1 },
  ];

  /**
   * Position-only view of the antennas of a single-gateway site. Kept for backward-compat
   * with map-preview (which still takes a flat antennas list). Computed on demand from
   * `gatewayAntennas`; empty when the site has several gateways. Use {@link gatewayAntennas}
   * for the per-gateway iteration.
   */
  readonly antennaPositions = computed<AntennaPosition[]>(() => {
    const gws = this.selectedGateways();
    if (gws.length !== 1) {
      return [];
    }
    const gwId = gws[0].id!;
    const list = this.gatewayAntennas()[gwId] ?? [];
    return list.map(a => ({ latitude: a.latitude, longitude: a.longitude, altitude: a.altitude }));
  });

  // Computed readiness signal
  readonly readiness = computed<ConfigReadiness>(() => {
    const gw = this.selectedGateways();
    const ants = this.gatewayAntennas();
    const size = this.selectedSize();

    const gatewaysSelected = gw.length > 0;
    const gatewaysWithPosition = gw.filter(
      g =>
        g.latitude != null &&
        g.longitude != null &&
        g.altitude != null &&
        !isNaN(g.latitude) &&
        !isNaN(g.longitude) &&
        !isNaN(g.altitude)
    ).length;
    const allGatewaysPositioned = gatewaysSelected && gatewaysWithPosition === gw.length;

    // V8 antenna check — every gateway must have at least one antenna and every
    // antenna must be fully positioned. Counted as a tuple (ok / total).
    let antennasOk = 0;
    let antennasTotal = 0;
    for (const g of gw) {
      const list = ants[g.id ?? ''] ?? [];
      antennasTotal += list.length;
      for (const a of list) {
        if (
          a.latitude != null &&
          a.longitude != null &&
          a.altitude != null &&
          !isNaN(a.latitude) &&
          !isNaN(a.longitude) &&
          !isNaN(a.altitude)
        ) {
          antennasOk += 1;
        }
      }
      // A gateway with zero antennas is invalid in V8.
      if (list.length === 0) antennasTotal = Math.max(1, antennasTotal);
    }
    const antennasReady = antennasTotal > 0 && antennasOk === antennasTotal;

    const freqLoaded = this.frequenciesLoaded();
    const calibLoaded = this.globalCalibLoaded();
    const blocked = this.frequencyBlocked();
    const filesReady = freqLoaded && calibLoaded && !blocked;
    const filesCount = [freqLoaded, calibLoaded].filter(Boolean).length;

    const sizeReady = size != null;

    // Hardware label
    const hwLabel = `Gateways (${gw.length})`;

    // Positions label — combines gateway positions + antennas count.
    const posLabel = `Positions (${gatewaysWithPosition}/${gw.length} gw · ${antennasOk}/${antennasTotal} ant)`;

    const hardwareDone = gatewaysSelected;

    const positionsDone = allGatewaysPositioned && antennasReady;

    return {
      hardware: { done: hardwareDone, label: hwLabel },
      positions: { done: positionsDone, label: posLabel },
      files: { done: filesReady, count: filesCount, total: 2, label: `Files (${filesCount}/2)`, blocked },
      size: { done: sizeReady },
      canValidate: hardwareDone && positionsDone && filesReady && sizeReady,
    };
  });

  // Dirty tracking
  readonly isDirty = signal(false);
  private initialSnapshot: string | null = null;
  /** Fichier de fréquences, calib ou source changés depuis le dernier save (hors instantané). */
  #filesChanged = false;

  private getStateSnapshot(): string {
    return JSON.stringify({
      gateways: this.selectedGateways().map(g => ({ id: g.id, lat: g.latitude, lng: g.longitude, alt: g.altitude })),
      antennas: this.gatewayAntennas(),
      size: this.selectedSize()?.value ?? null,
    });
  }

  captureInitialState(): void {
    this.initialSnapshot = this.getStateSnapshot();
    // Un fichier importé ou un preset appliqué (« Appliquer à un site ») avant la capture reste à valider.
    this.isDirty.set(this.#filesChanged);
  }

  markDirty(): void {
    if (this.initialSnapshot && this.getStateSnapshot() !== this.initialSnapshot) {
      this.isDirty.set(true);
      this.saveDraft();
    }
  }

  /** Fichier importé ou retiré du brouillon, source changée : la configuration est à valider. */
  markFilesChanged(): void {
    this.#filesChanged = true;
    this.isDirty.set(true);
  }

  markClean(): void {
    this.#filesChanged = false;
    this.isDirty.set(false);
    this.clearDraft();
    this.initialSnapshot = this.getStateSnapshot();
  }

  private getDraftKey(): string {
    return `wheere_config_draft_${this.siteId()}`;
  }

  private saveDraft(): void {
    const id = this.siteId();
    if (!id) return;
    const draft = {
      timestamp: Date.now(),
      gateways: this.selectedGateways(),
      antennas: this.gatewayAntennas(),
    };
    try {
      localStorage.setItem(this.getDraftKey(), JSON.stringify(draft));
    } catch {
      // Ignoré.
    }
  }

  clearDraft(): void {
    const id = this.siteId();
    if (id) {
      localStorage.removeItem(this.getDraftKey());
    }
  }

  hasDraft(): boolean {
    const id = this.siteId();
    if (!id) return false;
    return localStorage.getItem(this.getDraftKey()) !== null;
  }

  getDraftTimestamp(): Date | null {
    const id = this.siteId();
    if (!id) return null;
    try {
      const raw = localStorage.getItem(this.getDraftKey());
      if (!raw) return null;
      const draft = JSON.parse(raw);
      return new Date(draft.timestamp);
    } catch {
      return null;
    }
  }

  restoreDraft(): boolean {
    const id = this.siteId();
    if (!id) return false;
    try {
      const raw = localStorage.getItem(this.getDraftKey());
      if (!raw) return false;
      const draft = JSON.parse(raw);
      this.selectedGateways.set(draft.gateways ?? []);
      // V8 — drafts now carry the per-gateway antennas map. Legacy drafts with a flat
      // `antennas: AntennaPosition[]` array are migrated into the first gateway's antenna
      // list so the user doesn't lose their work mid-upgrade.
      if (draft.antennas && !Array.isArray(draft.antennas)) {
        this.gatewayAntennas.set(draft.antennas);
      } else if (Array.isArray(draft.antennas) && draft.gateways?.[0]?.id) {
        const migrated: AntennaDraft[] = (draft.antennas as AntennaPosition[]).map((a, i) => ({
          orderGateway: i,
          latitude: a.latitude,
          longitude: a.longitude,
          altitude: a.altitude,
          rfOut: null,
        }));
        this.gatewayAntennas.set({ [draft.gateways[0].id]: migrated });
      } else {
        this.gatewayAntennas.set({});
      }
      // ADR-019 — drafts no longer carry a mode; a legacy `mode` field is simply ignored.
      this.isDirty.set(true);
      return true;
    } catch {
      return false;
    }
  }

  setFrequenciesLoaded(loaded: boolean): void {
    this.frequenciesLoaded.set(loaded);
  }

  setGlobalCalibLoaded(loaded: boolean): void {
    this.globalCalibLoaded.set(loaded);
  }

  setSite(id: string): void {
    this.siteId.set(id);
    this.loadSiteConfiguration();
  }

  private loadSiteConfiguration(): void {
    this.loadAvailableGateways();
    const initialGateways = this.sitesStore.selectedGateways() || [];
    this.selectedGateways.set(initialGateways);

    // Seed gatewayAntennas from each gateway's backend antennas[] (V8) or fall back
    // to a single antenna at the gateway's coordinates.
    const antennas: Record<string, AntennaDraft[]> = {};
    for (const g of initialGateways) {
      if (!g.id) continue;
      antennas[g.id] = this.seedAntennasForGateway(g);
    }
    this.gatewayAntennas.set(antennas);

    // Derive the per-gateway custom-position flag from the persisted data — the flag itself is
    // never sent to the backend, so a fresh load must reconstruct it or the gateway position
    // would vanish after a refresh (and be clobbered by antenna 0 on the next save). Mirrors
    // the integrator: a 1-antenna gateway whose saved chassis coords diverge from antenna 0 was
    // explicitly placed → custom; coincident → implicit. Multi-antenna gateways are always
    // custom, handled directly by isGatewayPositionCustom().
    const custom: Record<string, boolean> = {};
    for (const g of initialGateways) {
      if (!g.id) continue;
      const ants = antennas[g.id] ?? [];
      const a0 = ants[0];
      custom[g.id] =
        ants.length === 1 &&
        g.latitude != null &&
        g.longitude != null &&
        (g.latitude !== a0?.latitude || g.longitude !== a0?.longitude);
    }
    this.customGatewayPosition.set(custom);

    this.frequenciesLoaded.set(false);
    this.globalCalibLoaded.set(false);
    this.frequencyBlocked.set(false);
  }

  /**
   * Seed the antenna list for a freshly-attached gateway:
   *  - If the backend already returned `Gateway.antennas`, use that (sorted by orderGateway).
   *  - Otherwise default to 1 antenna at the gateway's own coordinates so the UI is never
   *    empty (the user can bump the count up to 4 via the +/− buttons).
   */
  private seedAntennasForGateway(g: Gateway): AntennaDraft[] {
    const fromBackend = g.antennas ?? [];
    if (fromBackend.length > 0) {
      return [...fromBackend]
        .sort((a, b) => (a.orderGateway ?? 0) - (b.orderGateway ?? 0))
        .map((a, i) => ({
          orderGateway: a.orderGateway ?? i,
          latitude: a.latitude,
          longitude: a.longitude,
          altitude: a.altitude,
          rfOut: typeof a.rfOut === 'number' ? a.rfOut : null,
        }));
    }
    return [
      {
        orderGateway: 0,
        latitude: g.latitude,
        longitude: g.longitude,
        altitude: g.altitude,
        rfOut: null,
      },
    ];
  }

  /**
   * Organisation du site sélectionné (`customerId` = miroir legacy). `null` ⇒ aucun device
   * n'est assignable : le backend refuse (`SITE_HAS_NO_ORGANIZATION`).
   */
  siteOrganizationId(): string | null {
    const site = this.sitesStore.selected();
    return site?.organizationId ?? site?.customerId ?? null;
  }

  /**
   * Règle backend (gatewaymanager / sitemanager) : une gateway n'est assignable à un site que si
   * elle a une organisation ET que c'est celle du site. Sans organisation, ou d'une autre
   * organisation, le `PATCH /sites/{id}/layout` est refusé — on ne la propose donc pas.
   */
  isAssignableGateway(gateway: Gateway | null | undefined): boolean {
    const siteOrgId = this.siteOrganizationId();
    return !!siteOrgId && !!gateway?.organizationId && gateway.organizationId === siteOrgId;
  }

  loadAvailableGateways(): void {
    // Seules les gateways de l'organisation du site sont proposées. Les orphelines (sans
    // organisation) ne le sont pas : il faut d'abord leur assigner une organisation
    // (Management). Un site sans organisation n'a aucun device assignable — liste vide
    // (l'ancien repli « toute la liste » ne proposait que des choix voués au refus).
    const siteOrgId = this.siteOrganizationId();
    if (!siteOrgId) {
      this.availableGateways.set([]);
      return;
    }

    // Filtre serveur via `organizationId` (requis pour PLATFORM_*, écrasé par l'org du JWT
    // pour ORG_*). Le filtre client ci-dessous revérifie l'organisation (défense en
    // profondeur) et exclut les gateways déjà sélectionnées.
    this.http
      .get<Gateway[]>(`${this.apiBasePath}/gateways/without-site?organizationId=${siteOrgId}`)
      .pipe(
        map(gateways => {
          const selectedGatewayIds = this.selectedGateways().map(g => g.id);
          return (
            gateways?.filter(g => {
              if (!g || !g.id) return false;
              if (selectedGatewayIds?.includes(g.id)) return false;
              return this.isAssignableGateway(g);
            }) ?? []
          );
        }),
        tap(filteredGateways => {
          this.availableGateways.set(filteredGateways);
        })
      )
      .subscribe();
  }

  updateSelectedGateways(gateways: Gateway[]): void {
    const unique = [...new Map(gateways.map(g => [g.id, g])).values()];
    this.selectedGateways.set(unique);

    // Keep gatewayAntennas in sync — seed new entries, drop removed ones.
    const current = this.gatewayAntennas();
    const next: Record<string, AntennaDraft[]> = {};
    for (const g of unique) {
      if (!g.id) continue;
      next[g.id] = current[g.id] ?? this.seedAntennasForGateway(g);
    }
    this.gatewayAntennas.set(next);
    this.markDirty();
  }

  updateGatewayPosition(index: number, position: GatewayPosition): void {
    const gateways = [...this.selectedGateways()];
    if (index >= 0 && index < gateways.length) {
      gateways[index] = {
        ...gateways[index],
        latitude: position.latitude,
        longitude: position.longitude,
        altitude: position.altitude,
      };
      this.selectedGateways.set(gateways);
      this.markDirty();
    }
  }

  /**
   * True when a gateway exposes its own editable chassis position: multi-antenna
   * gateways always do; single-antenna ones only when the custom toggle is on. Drives
   * whether the gateway card shows a position row + the map draws a distinct marker.
   */
  isGatewayPositionCustom(gatewayId: string | undefined): boolean {
    if (!gatewayId) return false;
    const antennas = this.gatewayAntennas()[gatewayId] ?? [];
    if (antennas.length > 1) return true;
    return this.customGatewayPosition()[gatewayId] ?? false;
  }

  /** Toggle a single-antenna gateway's custom-position flag. */
  setCustomGatewayPosition(gatewayId: string, value: boolean): void {
    this.customGatewayPosition.set({ ...this.customGatewayPosition(), [gatewayId]: value });
    this.markDirty();
  }

  /**
   * Effective chassis position for a gateway: its own coordinates when the position is
   * custom (or multi-antenna), else antenna 0 (the implicit single-antenna case). Used
   * at save time so an implicit gateway always persists exactly its antenna-0 point,
   * matching the integrator.
   */
  resolveGatewayPosition(
    gatewayId: string | undefined,
    own: { latitude: number | undefined; longitude: number | undefined; altitude: number | undefined }
  ): { latitude: number | undefined; longitude: number | undefined; altitude: number | undefined } {
    // Fall back to antenna 0 when the gateway is implicit OR when a "custom" gateway has no
    // complete position of its own — persisting a half-null chassis point would drop the
    // gateway's position in the backend.
    const ownComplete = own.latitude != null && own.longitude != null && own.altitude != null;
    if (gatewayId && (!this.isGatewayPositionCustom(gatewayId) || !ownComplete)) {
      const a0 = (this.gatewayAntennas()[gatewayId] ?? [])[0];
      if (a0 && a0.latitude != null && a0.longitude != null && a0.altitude != null) {
        return { latitude: a0.latitude, longitude: a0.longitude, altitude: a0.altitude };
      }
    }
    return own;
  }

  /**
   * V8 — set the antenna count (1..4) for one gateway. Growing pads with empty
   * antennas; shrinking truncates from the end.
   */
  setAntennaCount(gatewayId: string, count: number): void {
    const clamped = Math.max(1, Math.min(4, Math.round(count)));
    const current = this.gatewayAntennas();
    const list = [...(current[gatewayId] ?? [])];
    if (clamped > list.length) {
      for (let i = list.length; i < clamped; i++) {
        list.push({
          orderGateway: i,
          latitude: undefined,
          longitude: undefined,
          altitude: undefined,
          rfOut: null,
        });
      }
    } else if (clamped < list.length) {
      list.length = clamped;
    }
    this.gatewayAntennas.set({ ...current, [gatewayId]: list });
    this.markDirty();
  }

  /**
   * V8 — update a specific antenna's position. Replaces the old flat
   * `updateAntennaPosition(index, position)` (single-gateway only).
   */
  updateAntennaPositionV8(gatewayId: string, antennaIndex: number, position: AntennaPosition): void {
    const current = this.gatewayAntennas();
    const list = [...(current[gatewayId] ?? [])];
    if (antennaIndex < 0 || antennaIndex >= list.length) return;
    list[antennaIndex] = {
      ...list[antennaIndex],
      latitude: position.latitude,
      longitude: position.longitude,
      altitude: position.altitude,
    };
    this.gatewayAntennas.set({ ...current, [gatewayId]: list });
    this.markDirty();
  }

  /**
   * V8 — set the RF OUT pin (0..3) or null (AUTO = positional default) for one antenna.
   * Enforces uniqueness within the gateway: if the chosen pin was held by another antenna
   * on the same gateway, that other antenna falls back to AUTO so we never persist a
   * conflicting (gateway_id, rf_out) pair (mirrors the backend partial-unique constraint).
   */
  setAntennaRfOut(gatewayId: string, antennaIndex: number, rfOut: number | null): void {
    const current = this.gatewayAntennas();
    const list = [...(current[gatewayId] ?? [])];
    if (antennaIndex < 0 || antennaIndex >= list.length) return;
    if (rfOut !== null) {
      for (let i = 0; i < list.length; i++) {
        if (i !== antennaIndex && list[i].rfOut === rfOut) {
          list[i] = { ...list[i], rfOut: null };
        }
      }
    }
    list[antennaIndex] = { ...list[antennaIndex], rfOut };
    this.gatewayAntennas.set({ ...current, [gatewayId]: list });
    this.markDirty();
  }

  /**
   * Is this RF OUT pin already taken by ANOTHER antenna on the same gateway? Used by
   * the segmented control to grey-out occupied pins (so the operator can't double-wire
   * two antennas onto the same hardware output).
   */
  isRfOutTaken(gatewayId: string, antennaIndex: number, pin: number): boolean {
    const list = this.gatewayAntennas()[gatewayId] ?? [];
    for (let i = 0; i < list.length; i++) {
      if (i !== antennaIndex && list[i].rfOut === pin) return true;
    }
    return false;
  }

  /**
   * @deprecated V8 — flat antenna update (single-gateway site). Routes to the new per-gateway
   * setter; kept so older callers (CSV importer, position dialog) keep working until
   * they're switched to the (gatewayId, antennaIndex) form.
   */
  updateAntennaPosition(index: number, position: AntennaPosition): void {
    const gw = this.selectedGateways();
    if (gw.length === 0 || !gw[0].id) return;
    this.updateAntennaPositionV8(gw[0].id, index, position);
  }

  /**
   * V8 unified save — identical to the integrator's flow.
   *
   * <p>Two steps, in order:
   *   1. Store the frequency file and / or calib imported since the last save (ADR-039), via
   *      {@code PUT /sites/{id}/files/{kind}} (multipart, kind `frequency_file` then
   *      `calib_file` : the server checks the calib rows against the frequency file in place).
   *      Files the operator didn't touch are left untouched on the site. A 422 refusal stops
   *      here: the previous file stays in place.
   *   2. {@code PATCH /sites/{id}/layout} with the unified body — gateways +
   *      per-gateway antennas (with RF OUT) + size. The backend checks the frequency setup
   *      (422 `FREQUENCY_SETUP_INVALID`), generates one signal.json per gateway and republishes
   *      MQTT — the exact same code path the integrator triggers.
   *
   * <p>ADR-019 — the only configuration path (the legacy {@code validate*} endpoints are gone).
   */
  saveViaLayout(
    selectedSize: SizeOption | undefined,
    files: SaveLayoutFiles,
    /**
     * Preset choisi quand la section fichiers est en mode "From preset". Persisté dans le
     * layout (`active_preset_id`) pour que le picker ré-affiche le bon preset au reload —
     * sinon le choix est perdu et l'ancien preset réapparaît. En mode preset les boutons
     * d'upload/suppression sont masqués, donc les fichiers envoyés sont ceux du preset :
     * aucun conflit avec le presetId.
     */
    presetId?: string | null
  ): Observable<SaveLayoutResult> {
    const siteId = this.siteId();
    if (!siteId) {
      return of({ success: false, errorKey: 'siteConfiguration.errors.noSiteId' });
    }

    const gateways = this.selectedGateways();
    if (gateways.length === 0) {
      return of({ success: false, errorKey: 'siteConfiguration.errors.noGatewayToValidate' });
    }

    const antennaMap = this.gatewayAntennas();

    // Build the layout body (mirrors integrator's UpdateSiteLayoutBody).
    const body: SiteConfigLayoutBody = {
      size: selectedSize?.value,
      gateways: gateways
        .filter(g => g.id)
        .map((g, idx) => {
          // Implicit (single-antenna, non-custom) gateways persist their antenna-0 point;
          // custom / multi-antenna gateways keep their own chassis coordinates.
          const pos = this.resolveGatewayPosition(g.id, {
            latitude: g.latitude,
            longitude: g.longitude,
            altitude: g.altitude,
          });
          return {
            gatewayId: g.id!,
            orderSite: idx,
            latitude: pos.latitude!,
            longitude: pos.longitude!,
            altitude: pos.altitude!,
          };
        }),
      gatewayAntennas: gateways
        .filter(g => g.id)
        .map(g => ({
          gatewayId: g.id!,
          antennas: (antennaMap[g.id!] ?? []).map((a, ai) => ({
            orderGateway: a.orderGateway ?? ai,
            latitude: a.latitude!,
            longitude: a.longitude!,
            altitude: a.altitude!,
            // null ⇒ omit so the backend default (rfOut = orderGateway) kicks in.
            ...(a.rfOut != null ? { rfOut: a.rfOut } : {}),
          })),
        })),
      // Persiste le preset actif si la section fichiers est en mode preset (cf. param).
      ...(presetId ? { presetId } : {}),
    };

    // Step 1 — store the files imported since the last save, one after the other (frequency file
    // first: the calib rows are checked against it), then step 2 — PATCH layout.
    const uploads: Observable<unknown>[] = [];
    if (files.frequencyRawContent && files.frequencyFileName) {
      uploads.push(this.uploadSiteFile(siteId, 'frequency_file', files.frequencyRawContent, files.frequencyFileName));
    }
    if (files.globalCalibRawContent && files.globalCalibFileName) {
      uploads.push(this.uploadSiteFile(siteId, 'calib_file', files.globalCalibRawContent, files.globalCalibFileName));
    }

    const upload$ = uploads.length > 0 ? concat(...uploads).pipe(last()) : of(null);

    return upload$.pipe(
      switchMap(() => this.http.patch<void>(`${this.apiBasePath}/sites/${siteId}/layout`, body)),
      map((): SaveLayoutResult => ({ success: true })),
      // Refus 422 du fichier de fréquences / calib ou de la validation (ADR-039) : chaque motif.
      // Sinon message métier du backend en priorité (ex. refus d'une gateway sans organisation ou
      // d'une autre organisation, qui cite le deviceId) : lu par `errorMessage`.
      catchError(error => {
        const rejection = frequencyRejection(error);
        if (rejection) {
          const lines = rejectionLines(rejection);
          return of<SaveLayoutResult>({ success: false, error: rejection.message || lines[0], errors: lines });
        }
        const message = errorMessage(error, '');
        return of<SaveLayoutResult>(
          message
            ? { success: false, error: message }
            : { success: false, errorKey: 'siteConfiguration.errors.unknown' }
        );
      }),
      shareReplay(1)
    );
  }

  /** PUT /sites/{id}/files/{kind} — stocke le fichier de fréquences ou le calib (multipart, ADR-039). */
  private uploadSiteFile(siteId: string, kind: string, content: string, filename: string): Observable<unknown> {
    const form = new FormData();
    const type = kind === 'frequency_file' ? 'application/json' : 'text/plain';
    form.append('file', new File([content], filename, { type }));
    return this.http.put(`${this.apiBasePath}/sites/${siteId}/files/${kind}`, form);
  }
}
